import type { VideoFrame } from '../types';

/**
 * コアのピクセルフォーマット → ImageData 互換の RGBA への変換。
 *
 * メインスレッドの Canvas2D 版と Worker の OffscreenCanvas 版、
 * さらに WebGL 版が同じ変換を使う。ここを 1 か所にしておかないと、
 * 「片方の経路だけ色がおかしい」種類の不具合が出る。
 */

/** ImageData の Uint32 ビューはリトルエンディアン環境で 0xAABBGGRR。 */
const LITTLE_ENDIAN = new Uint8Array(new Uint16Array([1]).buffer)[0] === 1;

export function pack(r: number, g: number, b: number): number {
  return LITTLE_ENDIAN
    ? (0xff << 24) | (b << 16) | (g << 8) | r
    : (r << 24) | (g << 16) | (b << 8) | 0xff;
}

export function convertRGB565(src: Uint16Array, dst: Uint32Array, frame: VideoFrame): void {
  const { width, height } = frame;
  // pitch はバイト単位。RGB565 は 1px=2byte。
  const srcStride = (frame.pitch || width * 2) >> 1;

  for (let y = 0; y < height; y++) {
    let si = y * srcStride;
    let di = y * width;
    for (let x = 0; x < width; x++, si++, di++) {
      const px = src[si] ?? 0;
      // 5/6/5 → 8/8/8 は上位ビットを下位に複製して伸長する（単純シフトより正確）
      const r5 = (px >> 11) & 0x1f;
      const g6 = (px >> 5) & 0x3f;
      const b5 = px & 0x1f;
      dst[di] = pack((r5 << 3) | (r5 >> 2), (g6 << 2) | (g6 >> 4), (b5 << 3) | (b5 >> 2));
    }
  }
}

export function convertXRGB8888(src: Uint32Array, dst: Uint32Array, frame: VideoFrame): void {
  const { width, height } = frame;
  const srcStride = (frame.pitch || width * 4) >> 2;

  for (let y = 0; y < height; y++) {
    let si = y * srcStride;
    let di = y * width;
    for (let x = 0; x < width; x++, si++, di++) {
      const px = src[si] ?? 0;
      dst[di] = pack((px >> 16) & 0xff, (px >> 8) & 0xff, px & 0xff);
    }
  }
}

/** フレームを RGBA へ変換する。dst8 は dst32 と同じバッファのビューであること。 */
export function convertFrame(frame: VideoFrame, dst32: Uint32Array, dst8: Uint8Array | Uint8ClampedArray): void {
  switch (frame.format) {
    case 'RGBA8888':
      dst8.set(frame.data as Uint8ClampedArray);
      return;
    case 'XRGB8888':
      convertXRGB8888(frame.data as Uint32Array, dst32, frame);
      return;
    case 'RGB565':
      convertRGB565(frame.data as Uint16Array, dst32, frame);
      return;
  }
}
